import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Loader2 } from 'lucide-react';
import type { Transaction, AllocationStrategy } from '@/types/payments';

interface AutoAllocateDropdownProps {
  receiptAmount: number;
  allocatedAmount: number;
  transactions: Transaction[];
  promiseDetected: boolean;
  onAllocate: (strategy: AllocationStrategy) => void;
  disabled?: boolean;
}

export function AutoAllocateDropdown({
  receiptAmount,
  allocatedAmount,
  transactions,
  promiseDetected,
  onAllocate,
  disabled = false,
}: AutoAllocateDropdownProps) {
  const [isCalculating, setIsCalculating] = useState(false);
  const [previewEnabled, setPreviewEnabled] = useState(
    localStorage.getItem('autoAllocatePreview') === 'true'
  );

  const availableAmount = receiptAmount - allocatedAmount;
  const hasTransactions = transactions.length > 0;
  const isDisabled = disabled || !hasTransactions || availableAmount <= 0;

  const handleStrategySelect = async (strategy: AllocationStrategy) => {
    setIsCalculating(true);

    // Simulate calculation delay for better UX
    await new Promise(resolve => setTimeout(resolve, 100));

    setIsCalculating(false);
    onAllocate(strategy);
  };

  const handlePreviewToggle = (checked: boolean) => {
    setPreviewEnabled(checked);
    localStorage.setItem('autoAllocatePreview', checked.toString());
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={isDisabled || isCalculating}
          className="min-w-[160px]"
        >
          {isCalculating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              Auto Allocate
              <ChevronDown className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[220px]">
        <DropdownMenuItem onClick={() => handleStrategySelect('oldest')}>
          <div className="flex flex-col">
            <span className="font-medium">Oldest First</span>
            <span className="text-xs text-muted-foreground">
              Allocate to oldest transactions
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => handleStrategySelect('largest')}>
          <div className="flex flex-col">
            <span className="font-medium">Largest First</span>
            <span className="text-xs text-muted-foreground">
              Allocate to largest balances
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => handleStrategySelect('smallest')}>
          <div className="flex flex-col">
            <span className="font-medium">Smallest First</span>
            <span className="text-xs text-muted-foreground">
              Pay off small debts first
            </span>
          </div>
        </DropdownMenuItem>

        {promiseDetected && (
          <DropdownMenuItem onClick={() => handleStrategySelect('promise')}>
            <div className="flex flex-col">
              <span className="font-medium">🎯 Promise Priority</span>
              <span className="text-xs text-muted-foreground">
                Allocate based on promise
              </span>
            </div>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={() => handleStrategySelect('due_date')}>
          <div className="flex flex-col">
            <span className="font-medium">Due Date Priority</span>
            <span className="text-xs text-muted-foreground">
              Allocate to most overdue
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuCheckboxItem
          checked={previewEnabled}
          onCheckedChange={handlePreviewToggle}
        >
          Preview Before Applying
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
